import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  increment,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import { useNotificationsStore } from '@/stores/notifications'
import type {
  Requirement,
  RequirementType,
  Priority,
  DefinitionStatus,
  DevelopmentStatus,
  DoDAItem,
  AcceptanceCriterion,
} from '@/types'
import type { RequirementsState } from './state'
import { useProjectsStore } from '@/views/Projects/store'

export interface CreateRequirementData {
  projectId: string
  organizationId: string
  applicationId?: string
  epicId?: string
  parentId?: string
  title: string
  description: string
  userStory?: {
    asA: string
    iWant: string
    soThat: string
  }
  type: RequirementType
  priority: Priority
  definitionStatus?: DefinitionStatus
  developmentStatus?: DevelopmentStatus
  storyPoints?: number
  estimatedHours?: number
  definitionOfDone?: DoDAItem[]
  acceptanceCriteria?: AcceptanceCriterion[]
  assignedTo?: string
  reporterId?: string
  dueDate?: Date
  releaseId?: string
  sprintId?: string
  blockedByIds?: string[]
  labels?: string[]
  clientNotes?: string
  internalNotes?: string
}

export interface UpdateRequirementData {
  epicId?: string
  parentId?: string
  title?: string
  description?: string
  userStory?: {
    asA: string
    iWant: string
    soThat: string
  }
  type?: RequirementType
  priority?: Priority
  definitionStatus?: DefinitionStatus
  developmentStatus?: DevelopmentStatus
  storyPoints?: number
  estimatedHours?: number
  actualHours?: number
  definitionOfDone?: DoDAItem[]
  acceptanceCriteria?: AcceptanceCriterion[]
  assignedTo?: string
  reporterId?: string
  startedAt?: Date
  completedAt?: Date
  dueDate?: Date
  releaseId?: string
  sprintId?: string
  blockedByIds?: string[]
  labels?: string[]
  clientNotes?: string
  internalNotes?: string
}

let globalUnsubscribe: Unsubscribe | null = null

export const createActions = (state: RequirementsState) => {
  const { logCreate, logUpdate, logDelete, detectChanges } = useActivityLog()
  const notificationsStore = useNotificationsStore()
  let snapshotPausedUntil = 0

  const pauseSnapshot = (ms = 1500) => {
    snapshotPausedUntil = Date.now() + ms
  }

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return

    const authStore = useAuthStore()
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []

    let q = query(
      collection(db, 'requirements'),
      ...baseConstraints,
      orderBy('createdAt', 'desc')
    )

    if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
      if (authStore.user.organizationId) {
        q = query(
          collection(db, 'requirements'),
          ...baseConstraints,
          where('organizationId', '==', authStore.user.organizationId),
          orderBy('createdAt', 'desc')
        )
      }
    }

    state.isLoading = true

    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      if (Date.now() < snapshotPausedUntil) return
      let requirements = snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          tenantId: data.tenantId,
          projectId: data.projectId,
          organizationId: data.organizationId,
          applicationId: data.applicationId,
          epicId: data.epicId,
          parentId: data.parentId,
          key: data.key || '',
          title: data.title,
          description: data.description,
          userStory: data.userStory,
          type: data.type || 'story',
          priority: data.priority,
          definitionStatus: data.definitionStatus,
          developmentStatus: data.developmentStatus || 'backlog',
          storyPoints: data.storyPoints,
          estimatedHours: data.estimatedHours,
          actualHours: data.actualHours,
          definitionOfDone: (data.definitionOfDone || []).map((d: Record<string, unknown>) => ({ ...d, completedAt: (d.completedAt as { toDate?: () => Date })?.toDate?.() || d.completedAt })),
          acceptanceCriteria: (data.acceptanceCriteria || []).map((a: Record<string, unknown>) => ({ ...a, completedAt: (a.completedAt as { toDate?: () => Date })?.toDate?.() || a.completedAt })),
          assignedTo: data.assignedTo,
          reporterId: data.reporterId,
          startedAt: data.startedAt?.toDate?.(),
          completedAt: data.completedAt?.toDate?.(),
          dueDate: data.dueDate?.toDate?.(),
          releaseId: data.releaseId,
          sprintId: data.sprintId,
          blockedByIds: data.blockedByIds || [],
          approvedAt: data.approvedAt?.toDate(),
          approvedBy: data.approvedBy,
          clientNotes: data.clientNotes,
          internalNotes: data.internalNotes,
          boardOrder: data.boardOrder,
          labels: data.labels || [],
          attachments: data.attachments || [],
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          createdBy: data.createdBy,
          updatedBy: data.updatedBy,
        } as Requirement
      })

      if (authStore.user?.role === 'operator') {
        const projectIds = new Set(useProjectsStore().projects.map(p => p.id))
        requirements = requirements.filter(r => projectIds.has(r.projectId))
      }

      state.requirements = requirements
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchRequirements = async (projectId?: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []
      let q = query(collection(db, 'requirements'), ...baseConstraints, orderBy('createdAt', 'desc'))

      // Filter by project if provided
      if (projectId) {
        q = query(
          collection(db, 'requirements'),
          ...baseConstraints,
          where('projectId', '==', projectId),
          orderBy('createdAt', 'desc')
        )
      } else if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
        // Filter by organization for clients
        if (authStore.user.organizationId) {
          q = query(
            collection(db, 'requirements'),
            ...baseConstraints,
            where('organizationId', '==', authStore.user.organizationId),
            orderBy('createdAt', 'desc')
          )
        }
      } else if (authStore.user?.role === 'operator') {
        const projectsStore = useProjectsStore()
        if (projectsStore.projects.length === 0) await projectsStore.fetchProjects()
        const projectIds = projectsStore.projects.map(p => p.id)
        if (projectIds.length === 0) {
          state.requirements = []
          state.isLoading = false
          return
        }
        // Fetch in batches of 30
        const allDocs: typeof state.requirements = []
        for (let i = 0; i < projectIds.length; i += 30) {
          const batch = projectIds.slice(i, i + 30)
          const batchQ = query(
            collection(db, 'requirements'),
            ...baseConstraints,
            where('projectId', 'in', batch),
            orderBy('createdAt', 'desc')
          )
          const snap = await getDocs(batchQ)
          allDocs.push(...snap.docs.map((doc) => {
            const data = doc.data()
            return {
              id: doc.id,
              tenantId: data.tenantId,
              projectId: data.projectId,
              organizationId: data.organizationId,
              applicationId: data.applicationId,
              epicId: data.epicId,
              parentId: data.parentId,
              key: data.key || '',
              title: data.title,
              description: data.description,
              userStory: data.userStory,
              type: data.type || 'story',
              priority: data.priority,
              definitionStatus: data.definitionStatus,
              developmentStatus: data.developmentStatus || 'backlog',
              storyPoints: data.storyPoints,
              estimatedHours: data.estimatedHours,
              actualHours: data.actualHours,
              definitionOfDone: (data.definitionOfDone || []).map((d: Record<string, unknown>) => ({ ...d, completedAt: (d.completedAt as { toDate?: () => Date })?.toDate?.() || d.completedAt })),
              acceptanceCriteria: (data.acceptanceCriteria || []).map((a: Record<string, unknown>) => ({ ...a, completedAt: (a.completedAt as { toDate?: () => Date })?.toDate?.() || a.completedAt })),
              assignedTo: data.assignedTo,
              reporterId: data.reporterId,
              startedAt: data.startedAt?.toDate?.(),
              completedAt: data.completedAt?.toDate?.(),
              dueDate: data.dueDate?.toDate?.(),
              releaseId: data.releaseId,
              sprintId: data.sprintId,
              blockedByIds: data.blockedByIds || [],
              approvedAt: data.approvedAt?.toDate(),
              approvedBy: data.approvedBy,
              clientNotes: data.clientNotes,
              internalNotes: data.internalNotes,
              boardOrder: data.boardOrder,
              labels: data.labels || [],
              attachments: data.attachments || [],
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date(),
              createdBy: data.createdBy,
              updatedBy: data.updatedBy,
            } as Requirement
          }))
        }
        state.requirements = allDocs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        state.isLoading = false
        return
      }

      const snapshot = await getDocs(q)

      state.requirements = snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          tenantId: data.tenantId,
          projectId: data.projectId,
          organizationId: data.organizationId,
          applicationId: data.applicationId,
          epicId: data.epicId,
          parentId: data.parentId,
          key: data.key || '',
          title: data.title,
          description: data.description,
          userStory: data.userStory,
          type: data.type || 'story',
          priority: data.priority,
          definitionStatus: data.definitionStatus,
          developmentStatus: data.developmentStatus || 'backlog',
          storyPoints: data.storyPoints,
          estimatedHours: data.estimatedHours,
          actualHours: data.actualHours,
          definitionOfDone: (data.definitionOfDone || []).map((d: Record<string, unknown>) => ({ ...d, completedAt: (d.completedAt as { toDate?: () => Date })?.toDate?.() || d.completedAt })),
          acceptanceCriteria: (data.acceptanceCriteria || []).map((a: Record<string, unknown>) => ({ ...a, completedAt: (a.completedAt as { toDate?: () => Date })?.toDate?.() || a.completedAt })),
          assignedTo: data.assignedTo,
          reporterId: data.reporterId,
          startedAt: data.startedAt?.toDate?.(),
          completedAt: data.completedAt?.toDate?.(),
          dueDate: data.dueDate?.toDate?.(),
          releaseId: data.releaseId,
          sprintId: data.sprintId,
          blockedByIds: data.blockedByIds || [],
          approvedAt: data.approvedAt?.toDate(),
          approvedBy: data.approvedBy,
          clientNotes: data.clientNotes,
          internalNotes: data.internalNotes,
          boardOrder: data.boardOrder,
          labels: data.labels || [],
          attachments: data.attachments || [],
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          createdBy: data.createdBy,
          updatedBy: data.updatedBy,
        } as Requirement
      })
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al cargar requerimientos'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const fetchRequirementById = async (requirementId: string): Promise<Requirement | null> => {
    try {
      const docRef = doc(db, 'requirements', requirementId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return null
      }

      const data = docSnap.data()
      return {
        id: docSnap.id,
        tenantId: data.tenantId,
        projectId: data.projectId,
        organizationId: data.organizationId,
        applicationId: data.applicationId,
        epicId: data.epicId,
        parentId: data.parentId,
        key: data.key || '',
        title: data.title,
        description: data.description,
        userStory: data.userStory,
        type: data.type || 'story',
        priority: data.priority,
        definitionStatus: data.definitionStatus,
        developmentStatus: data.developmentStatus || 'backlog',
        storyPoints: data.storyPoints,
        estimatedHours: data.estimatedHours,
        actualHours: data.actualHours,
        definitionOfDone: (data.definitionOfDone || []).map((d: Record<string, unknown>) => ({ ...d, completedAt: (d.completedAt as { toDate?: () => Date })?.toDate?.() || d.completedAt })),
        acceptanceCriteria: (data.acceptanceCriteria || []).map((a: Record<string, unknown>) => ({ ...a, completedAt: (a.completedAt as { toDate?: () => Date })?.toDate?.() || a.completedAt })),
        assignedTo: data.assignedTo,
        reporterId: data.reporterId,
        startedAt: data.startedAt?.toDate?.(),
        completedAt: data.completedAt?.toDate?.(),
        dueDate: data.dueDate?.toDate?.(),
        releaseId: data.releaseId,
        sprintId: data.sprintId,
        blockedByIds: data.blockedByIds || [],
        approvedAt: data.approvedAt?.toDate(),
        approvedBy: data.approvedBy,
        clientNotes: data.clientNotes,
        internalNotes: data.internalNotes,
        boardOrder: data.boardOrder,
        labels: data.labels || [],
        attachments: data.attachments || [],
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        createdBy: data.createdBy,
        updatedBy: data.updatedBy,
      } as Requirement
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al cargar requerimiento'
      throw error
    }
  }

  const createRequirement = async (data: CreateRequirementData): Promise<Requirement> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.userId || 'system'

      // Get project to generate the key
      const projectRef = doc(db, 'projects', data.projectId)
      const projectSnap = await getDoc(projectRef)

      if (!projectSnap.exists()) {
        throw new Error('Proyecto no encontrado')
      }

      const projectData = projectSnap.data()
      const reqCounter = (projectData.requirementCounter || 0) + 1
      const projectKey = projectData.key || 'PROJ'

      // Update project requirement counter
      await updateDoc(projectRef, {
        requirementCounter: increment(1),
        updatedAt: serverTimestamp(),
      })

      const firestoreData: Record<string, unknown> = {
        ...getTenantData(),
        projectId: data.projectId,
        organizationId: data.organizationId,
        key: `${projectKey}-${reqCounter}`,
        title: data.title,
        description: data.description,
        type: data.type,
        priority: data.priority,
        definitionStatus: data.definitionStatus || 'requested',
        developmentStatus: data.developmentStatus || 'backlog',
        definitionOfDone: data.definitionOfDone || [],
        acceptanceCriteria: data.acceptanceCriteria || [],
        blockedByIds: data.blockedByIds || [],
        labels: data.labels || [],
        attachments: [],
        reporterId: currentUserId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUserId,
        updatedBy: currentUserId,
      }

      // Only add optional fields if they have values
      if (data.applicationId) firestoreData.applicationId = data.applicationId
      if (data.epicId) firestoreData.epicId = data.epicId
      if (data.parentId) firestoreData.parentId = data.parentId
      if (data.userStory) firestoreData.userStory = data.userStory
      if (data.storyPoints) firestoreData.storyPoints = data.storyPoints
      if (data.estimatedHours) firestoreData.estimatedHours = data.estimatedHours
      if (data.assignedTo) firestoreData.assignedTo = data.assignedTo
      if (data.dueDate) firestoreData.dueDate = data.dueDate
      if (data.releaseId) firestoreData.releaseId = data.releaseId
      if (data.sprintId) firestoreData.sprintId = data.sprintId
      if (data.clientNotes) firestoreData.clientNotes = data.clientNotes
      if (data.internalNotes) firestoreData.internalNotes = data.internalNotes

      const docRef = await addDoc(collection(db, 'requirements'), firestoreData)

      const newRequirement: Requirement = {
        id: docRef.id,
        projectId: data.projectId,
        organizationId: data.organizationId,
        applicationId: data.applicationId,
        epicId: data.epicId,
        parentId: data.parentId,
        key: `${projectKey}-${reqCounter}`,
        title: data.title,
        description: data.description,
        userStory: data.userStory,
        type: data.type,
        priority: data.priority,
        definitionStatus: data.definitionStatus || 'requested',
        developmentStatus: data.developmentStatus || 'backlog',
        storyPoints: data.storyPoints,
        estimatedHours: data.estimatedHours,
        definitionOfDone: data.definitionOfDone || [],
        acceptanceCriteria: data.acceptanceCriteria || [],
        assignedTo: data.assignedTo,
        reporterId: currentUserId,
        dueDate: data.dueDate,
        releaseId: data.releaseId,
        blockedByIds: data.blockedByIds || [],
        labels: data.labels || [],
        clientNotes: data.clientNotes,
        internalNotes: data.internalNotes,
        attachments: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: currentUserId,
        updatedBy: currentUserId,
      }

      state.requirements.unshift(newRequirement)
      await logCreate('requirement', newRequirement.id, newRequirement.title)

      // Notify admins + assigned user about new requirement
      await notificationsStore.notifyAdminsAndStakeholders(
        [data.assignedTo].filter(Boolean) as string[],
        'requirement_created',
        'Nuevo requerimiento creado',
        `Se ha creado el requerimiento "${newRequirement.key}: ${newRequirement.title}"`,
        'requirement',
        newRequirement.id
      )

      return newRequirement
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al crear requerimiento'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const updateRequirement = async (
    requirementId: string,
    data: UpdateRequirementData
  ): Promise<Requirement> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const currentUserId = authStore.userId || 'system'

      // Get current requirement data for change detection
      const currentRequirement = state.requirements.find((r) => r.id === requirementId)
      if (!currentRequirement) {
        throw new Error('Requerimiento no encontrado')
      }

      const updateData: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      }

      // Only add fields that are being updated
      if (data.epicId !== undefined) updateData.epicId = data.epicId
      if (data.parentId !== undefined) updateData.parentId = data.parentId
      if (data.title !== undefined) updateData.title = data.title
      if (data.description !== undefined) updateData.description = data.description
      if (data.userStory !== undefined) updateData.userStory = data.userStory
      if (data.type !== undefined) updateData.type = data.type
      if (data.priority !== undefined) updateData.priority = data.priority
      if (data.definitionStatus !== undefined) updateData.definitionStatus = data.definitionStatus
      if (data.developmentStatus !== undefined) {
        updateData.developmentStatus = data.developmentStatus
        // Auto-set startedAt when moving to development
        if (data.developmentStatus === 'development' && !currentRequirement.startedAt) {
          updateData.startedAt = serverTimestamp()
        }
        // Auto-set completedAt when moving to production
        if (data.developmentStatus === 'production' && !currentRequirement.completedAt) {
          updateData.completedAt = serverTimestamp()
        }
      }
      if (data.storyPoints !== undefined) updateData.storyPoints = data.storyPoints
      if (data.estimatedHours !== undefined) updateData.estimatedHours = data.estimatedHours
      if (data.actualHours !== undefined) updateData.actualHours = data.actualHours
      if (data.definitionOfDone !== undefined) updateData.definitionOfDone = data.definitionOfDone
      if (data.acceptanceCriteria !== undefined) updateData.acceptanceCriteria = data.acceptanceCriteria
      if (data.assignedTo !== undefined) updateData.assignedTo = data.assignedTo
      if (data.reporterId !== undefined) updateData.reporterId = data.reporterId
      if (data.startedAt !== undefined) updateData.startedAt = data.startedAt
      if (data.completedAt !== undefined) updateData.completedAt = data.completedAt
      if (data.dueDate !== undefined) updateData.dueDate = data.dueDate
      if (data.releaseId !== undefined) updateData.releaseId = data.releaseId
      if (data.sprintId !== undefined) updateData.sprintId = data.sprintId
      if (data.blockedByIds !== undefined) updateData.blockedByIds = data.blockedByIds
      if (data.labels !== undefined) updateData.labels = data.labels
      if (data.clientNotes !== undefined) updateData.clientNotes = data.clientNotes
      if (data.internalNotes !== undefined) updateData.internalNotes = data.internalNotes

      await updateDoc(doc(db, 'requirements', requirementId), updateData)

      // Detect changes for activity log
      const changes = detectChanges(
        currentRequirement as unknown as Record<string, unknown>,
        data as Partial<Record<string, unknown>>,
        ['title', 'type', 'priority', 'definitionStatus', 'developmentStatus', 'assignedTo']
      )

      const updatedRequirement: Requirement = {
        ...currentRequirement,
        ...data,
        updatedAt: new Date(),
        updatedBy: currentUserId,
      }

      // Update in local state
      const index = state.requirements.findIndex((r) => r.id === requirementId)
      if (index !== -1) {
        state.requirements[index] = updatedRequirement
      }

      if (state.selectedRequirement?.id === requirementId) {
        state.selectedRequirement = updatedRequirement
      }

      if (changes.length > 0) {
        await logUpdate('requirement', requirementId, changes, updatedRequirement.title)
      }

      // Notify assigned + reporter on development status change
      if (data.developmentStatus && data.developmentStatus !== currentRequirement.developmentStatus) {
        const stakeholders = [currentRequirement.assignedTo, currentRequirement.reporterId].filter(Boolean) as string[]
        await notificationsStore.notifyUsers(
          stakeholders,
          'requirement_status_changed',
          'Requerimiento actualizado',
          `"${currentRequirement.key}: ${currentRequirement.title}" cambió a ${data.developmentStatus}`,
          'requirement',
          requirementId
        )
      }

      // Notify assigned + reporter on definition status change
      if (data.definitionStatus && data.definitionStatus !== currentRequirement.definitionStatus) {
        const stakeholders = [currentRequirement.assignedTo, currentRequirement.reporterId].filter(Boolean) as string[]
        await notificationsStore.notifyUsers(
          stakeholders,
          'requirement_status_changed',
          'Requerimiento actualizado',
          `"${currentRequirement.key}: ${currentRequirement.title}" cambió a ${data.definitionStatus}`,
          'requirement',
          requirementId
        )
      }

      // Notify on assignment change
      if (data.assignedTo && data.assignedTo !== currentRequirement.assignedTo) {
        await notificationsStore.createNotification(
          data.assignedTo,
          'requirement_assigned',
          'Requerimiento asignado',
          `Se te asignó "${currentRequirement.key}: ${currentRequirement.title}"`,
          'requirement',
          requirementId
        )
      }

      return updatedRequirement
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al actualizar requerimiento'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const deleteRequirement = async (requirementId: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const requirement = state.requirements.find((r) => r.id === requirementId)

      await deleteDoc(doc(db, 'requirements', requirementId))

      state.requirements = state.requirements.filter((r) => r.id !== requirementId)

      if (state.selectedRequirement?.id === requirementId) {
        state.selectedRequirement = null
      }

      if (requirement) {
        await logDelete('requirement', requirementId, requirement.title)
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al eliminar requerimiento'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const approveRequirement = async (requirementId: string): Promise<void> => {
    const authStore = useAuthStore()
    await updateRequirement(requirementId, { definitionStatus: 'approved' })

    // Update approval info
    await updateDoc(doc(db, 'requirements', requirementId), {
      approvedAt: serverTimestamp(),
      approvedBy: authStore.userId,
    })

    const index = state.requirements.findIndex((r) => r.id === requirementId)
    if (index !== -1) {
      state.requirements[index] = {
        ...state.requirements[index],
        approvedAt: new Date(),
        approvedBy: authStore.userId || undefined,
      }
    }
  }

  const rejectRequirement = async (requirementId: string): Promise<void> => {
    await updateRequirement(requirementId, { definitionStatus: 'rejected' })
  }

  const updateDefinitionStatus = async (
    requirementId: string,
    status: DefinitionStatus
  ): Promise<void> => {
    await updateRequirement(requirementId, { definitionStatus: status })
  }

  const updateDevelopmentStatus = async (
    requirementId: string,
    status: DevelopmentStatus
  ): Promise<void> => {
    await updateRequirement(requirementId, { developmentStatus: status })
  }

  const setSelectedRequirement = (requirement: Requirement | null): void => {
    state.selectedRequirement = requirement
  }

  const setFilters = (filters: Partial<RequirementsState['filters']>): void => {
    state.filters = { ...state.filters, ...filters }
  }

  const clearError = (): void => {
    state.error = null
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    pauseSnapshot,
    fetchRequirements,
    fetchRequirementById,
    createRequirement,
    updateRequirement,
    deleteRequirement,
    approveRequirement,
    rejectRequirement,
    updateDefinitionStatus,
    updateDevelopmentStatus,
    setSelectedRequirement,
    setFilters,
    clearError,
  }
}
